import React from 'react';

const responseFrame = 'mt-2 rounded-md bg-surface-alt p-3 text-sm text-text';

export const StudentSelfReviewGuide: React.FC = () => (
  <details className="rounded-xl border border-info/20 bg-info/5 p-4">
    <summary className="cursor-pointer font-semibold text-text">Student self-review instructions</summary>
    <div className="mt-4 space-y-5 text-sm text-muted">
      <div>
        <h2 className="font-semibold text-text">How to complete this review</h2>
        <p className="mt-1">Answer all five questions in your own words. Use a real example from this review period in every answer: name the work or situation, explain what you did, and describe the result.</p>
      </div>

      <ol className="space-y-4">
        <li>
          <h3 className="font-semibold text-text">1. Results and impact</h3>
          <p>Explain what you finished, produced, improved, or contributed. Include what you personally did, the result, and who benefited.</p>
          <p className={responseFrame}><span className="font-medium">Response frame:</span> During this period, I completed or contributed ____. I was responsible for ____. This resulted in ____, which helped ____.</p>
        </li>
        <li>
          <h3 className="font-semibold text-text">2. Evidence of quality or growth</h3>
          <p>Choose one or two pieces of work that best prove what you can do now. Name the work and explain the exact details that show quality, improvement, or learning.</p>
          <p className={responseFrame}><span className="font-medium">Response frame:</span> My ____ is strong evidence because it shows ____. One specific example is ____. Compared with my earlier work, ____.</p>
        </li>
        <li>
          <h3 className="font-semibold text-text">3. Problem-solving, feedback, and revision</h3>
          <p>Describe a real challenge or useful feedback. Focus on the action you took, what changed, and what you learned—not only on the problem.</p>
          <p className={responseFrame}><span className="font-medium">Response frame:</span> I encountered or received ____. I responded by ____. As a result, ____. I learned ____.</p>
        </li>
        <li>
          <h3 className="font-semibold text-text">4. Professional practice</h3>
          <p>Give an example of how you managed responsibilities, followed through, communicated, contributed to others, or sought and used support appropriately.</p>
          <p className={responseFrame}><span className="font-medium">Response frame:</span> I showed professional practice when ____. I ____, which helped or resulted in ____.</p>
        </li>
        <li>
          <h3 className="font-semibold text-text">5. Next-cycle plan</h3>
          <p>Name one priority, your first action, evidence that will show success, a real checkpoint date, and any support you need.</p>
          <p className={responseFrame}><span className="font-medium">Response frame:</span> My next priority is ____. My first action will be ____. By [date], I will check ____. I will know I am successful when ____. I need ____ to help me.</p>
        </li>
      </ol>

      <div>
        <h2 className="font-semibold text-text">Strength and growth priority</h2>
        <p className="mt-1">Choose two different performance areas: one demonstrated strength and one area to develop. For each, choose the rating your current evidence supports and name the specific work or behavior that proves it.</p>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li><span className="font-medium text-text">Non-Proficient:</span> I need frequent guidance.</li>
          <li><span className="font-medium text-text">Developing:</span> I demonstrate parts of the skill, but not yet consistently or independently.</li>
          <li><span className="font-medium text-text">Proficient:</span> I demonstrate the skill independently and consistently in expected situations.</li>
          <li><span className="font-medium text-text">Mastery:</span> I adapt and transfer the skill in more complex situations to improve quality.</li>
        </ul>
        <p className={responseFrame}><span className="font-medium">Evidence frame:</span> I rated myself ____ in ____ because, while working on ____, I ____. This shows ____.</p>
      </div>

      <div>
        <h2 className="font-semibold text-text">Before submitting</h2>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li>Complete all five written questions with specific examples.</li>
          <li>Select two different performance areas: one strength and one growth priority.</li>
          <li>Add a rating and specific evidence for both selections.</li>
          <li>Include a first action, success evidence, checkpoint date, and needed support in your next-cycle plan.</li>
        </ul>
        <p className="mt-2">Portfolio examples are optional. You may update a submitted review while the period remains open; after the period closes or the teacher finalizes the review, changes may no longer be available.</p>
      </div>
    </div>
  </details>
);
